import InputField from "components/fields/InputField";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ConfirmResponderRequest, ResponderRequest } from "types/authTypes";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { useEffect, useState } from "react";
import OtpInput from "components/otp/OtpInput";
import { formatTime } from "services/helper";
import { motion, AnimatePresence } from "framer-motion";
import Button from "components/button/Button";
import {
  setResponder,
  setResponderToken,
} from "reduxFiles/slices/responderAuthSlice";
import {
  useConfirmResponderLoginMutation,
  useResendResponderOtpMutation,
  useResponderLoginMutation,
} from "reduxFiles/api/responderApi";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi2";

export default function ResponderLogin() {
  const RESEND_TIME = 120;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isRtl = document.documentElement.dir === "rtl";

  // apis
  const [register, { isLoading: registerLoading }] =
    useResponderLoginMutation();
  const [confirm, { isLoading: confirmLoading }] =
    useConfirmResponderLoginMutation();
  const [resendOtp, { isLoading: resendLoading }] =
    useResendResponderOtpMutation();

  // states
  const [step, setStep] = useState<"register" | "confirm">("register");
  const [nationalCode, setNationalCode] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(RESEND_TIME);

  // timer
  useEffect(() => {
    if (step !== "confirm") return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  useEffect(() => {
    if (step === "confirm") {
      setTimer(RESEND_TIME);
    }
  }, [step]);

  // forms
  const {
    register: registerForm,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
  } = useForm<ResponderRequest>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { handleSubmit: handleConfirmSubmit } =
    useForm<ConfirmResponderRequest>({
      mode: "onChange",
    });

  const handleResend = async () => {
    try {
      await resendOtp({ mobile }).unwrap();
      setTimer(RESEND_TIME);
    } catch (err) {
      console.error(err);
    }
  };

  const onRegister = async (data: ResponderRequest) => {
    try {
      await register(data).unwrap();
      setMobile(data.mobile);
      setNationalCode(data.nationalCode);
      setStep("confirm");
    } catch (error) {
      console.error(error);
    }
  };

  const onConfirm = async (code: string) => {
    if (code.length < 4) {
      dispatch(
        addNotification({
          type: "error",
          message: t("auth.resetPassword.invalidCode"),
        })
      );
      return;
    }

    try {
      const result = await confirm({
        nationalCode,
        otp: code,
      }).unwrap();

      dispatch(setResponder(result.responder));
      dispatch(setResponderToken(result.accessToken));

      dispatch(
        addNotification({
          type: "success",
          message: t("auth.signIn.success"),
        })
      );

      navigate("/assessment");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex h-full w-full items-center justify-center"
    >
      <div className="w-full max-w-md px-2 sm:px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="mb-4 sm:mb-6"
        >
          <div className="mb-1 flex items-center gap-2">
            <div className="h-1 w-8 rounded-full bg-gradient-to-r from-brand-500 to-indigo-500" />
            <span className="text-xs font-medium uppercase tracking-wider text-brand-500 dark:text-brand-100">
              {t("auth.responderLogin.badge") || "Responder"}
            </span>
          </div>
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white sm:text-3xl">
            {t("auth.responderLogin.title")}
          </h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("auth.responderLogin.description")}
          </p>
        </motion.div>

        {/* Forms Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative"
        >
          <AnimatePresence mode="wait">
            {/* STEP 1 - Register */}
            {step === "register" && (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.4 }}
                onSubmit={handleRegisterSubmit(onRegister)}
                noValidate
                className="space-y-3"
              >
                {/* National Code & Mobile - Row */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <InputField
                    label={t("auth.fields.nationalCode")}
                    placeholder={t("auth.placeholders.nationalCode")}
                    autoFocus
                    id="nationalCode"
                    variant="auth"
                    extra="mb-0"
                    {...registerForm("nationalCode", {
                      required: t("validation.required"),
                      pattern: {
                        value: /^\d{10}$/,
                        message: t("validation.nationalCode"),
                      },
                    })}
                    state={registerErrors?.nationalCode ? "error" : ""}
                    errorMessage={registerErrors.nationalCode?.message}
                  />
                  <InputField
                    label={t("auth.fields.mobile")}
                    placeholder={t("auth.placeholders.mobile")}
                    id="mobile"
                    variant="auth"
                    extra="mb-0"
                    {...registerForm("mobile", {
                      required: t("validation.required"),
                      pattern: {
                        value: /^09\d{9}$/,
                        message: t("validation.mobile"),
                      },
                    })}
                    state={registerErrors.mobile ? "error" : ""}
                    errorMessage={registerErrors.mobile?.message}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={registerLoading}
                  className="mt-1"
                >
                  {t("auth.responderLogin.submit")}
                </Button>
              </motion.form>
            )}

            {/* STEP 2 - Confirm OTP */}
            {step === "confirm" && (
              <motion.form
                key="confirm"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
                onSubmit={handleConfirmSubmit(() => onConfirm(otp))}
                noValidate
                className="space-y-4"
              >
                {/* Back Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  startIcon={isRtl ? <HiArrowRight /> : <HiArrowLeft />}
                  onClick={() => setStep("register")}
                  className="w-fit"
                >
                  {t("buttons.back")}
                </Button>

                {/* OTP Input */}
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t("auth.signUp.otpSent", { name: mobile })}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {t("auth.signUp.enterCode")}
                    </p>
                  </div>

                  <OtpInput
                    length={4}
                    onChange={setOtp}
                    onComplete={(code) => {
                      onConfirm(code);
                    }}
                  />
                </div>

                {/* Resend Timer & Button */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2 dark:bg-navy-800/50">
                  {timer > 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("auth.signUp.resendIn", {
                        seconds: formatTime(timer),
                      })}
                    </p>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      loading={resendLoading}
                      onClick={handleResend}
                      disabled={resendLoading || timer > 0}
                      className="text-xs"
                    >
                      {t("auth.signUp.resendCode")}
                    </Button>
                  )}
                </div>

                {/* Confirm Button */}
                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  fullWidth
                  loading={confirmLoading}
                >
                  {t("auth.responderLogin.submitConfirm")}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-4 space-y-3 border-t border-gray-200 pt-4 dark:border-navy-700"
        >
          {/* Create Account */}
          <div className="flex flex-wrap items-center justify-center gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("auth.signIn.notRegistered")}
            </span>
            <Link
              to="/auth/sign-up"
              className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
            >
              {t("auth.signIn.createAccount")}
            </Link>
          </div>

          {/* Company Login */}
          <div className="flex flex-wrap items-center justify-center gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("auth.signIn.loginWithCompany")}
            </span>
            <Link
              to="/auth/sign-in"
              className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
            >
              {t("auth.signIn.loginCompanyButton")}
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
